import React, { useEffect, useState, useMemo } from "react";
import { Table, Form, Button, Modal, Badge } from "react-bootstrap";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  Plus,
  Car,
  Trash2,
  FileText,
  Download,
  RotateCw,
  Eye,
  CheckCircle2,
  Clock,
  DollarSign,
  Truck,
  ShieldCheck,
  CreditCard,
  RefreshCw,
  Sparkles,
  Users,
  TrendingUp,
  Receipt,
} from "lucide-react";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "../components/InvoicePDF";
import "./mycustomers.css";

const generateInvoiceNumber = (saleId) => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const sid = String(saleId || 1).padStart(4, "0");
  const rand = Math.floor(100 + Math.random() * 900);
  return `INV-${yy}${mm}-${sid}-${rand}`;
};

const cleanCustomerName = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  const cleaned = s.replace(/[^\w\s.,&'()#/-]/gi, "").trim();
  return cleaned || s;
};

const moneyInput = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const getInitials = (name) => {
  if (!name) return "PA";
  const clean = cleanCustomerName(name);
  const parts = clean.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase() || "PA";
};

const MyCustomers = () => {
  const [ledgers, setLedgers] = useState([]);
  const [stock, setStock] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [agentFilter, setAgentFilter] = useState("");

  // Modals
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Selected Context
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [payments, setPayments] = useState([]);

  // Forms
  const [saleForm, setSaleForm] = useState({ carId: "", cifPrice: "", saleDate: "" });
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "Bank Wire (Mizuho)",
    remarks: "",
  });
  const [editSaleForm, setEditSaleForm] = useState({
    cifPrice: "",
    shipping_status: "Pending",
    next_payment_reminder: "",
    notes: "",
  });

  // Multi-Vehicle Invoice State
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "",
    customerName: "",
    customerAddress: "NASSAU, BAHAMAS",
    customerContact: "",
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10),
    cars: [
      {
        maker: "",
        model: "",
        year: "",
        chassis: "",
        cc: "1500",
        transmission: "AUTOMATIC",
        amount: "0.00",
      },
    ],
    amount: "0.00",
    balance: "0.00",
    paymentLabel: "BALANCE PAYMENT AMOUNT",
    docType: "PRO-FORMA INVOICE",
  });

  useEffect(() => {
    fetchLedgers();
    api.get("/cars?status=Available").then((res) => setStock(res.data)).catch(() => {});
  }, []);

  const fetchLedgers = () => {
    api.get("/sales/ledger/all").then((res) => setLedgers(res.data)).catch(() => {});
  };

  const uniqueAgents = useMemo(() => {
    const s = new Set();
    ledgers.forEach(l => {
      if (l.agentName && l.agentName.trim()) s.add(l.agentName.trim());
    });
    return Array.from(s).sort();
  }, [ledgers]);

  const filteredLedgers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return ledgers.filter((l) => {
      if (agentFilter && (l.agentName || "").toLowerCase() !== agentFilter.toLowerCase()) {
        return false;
      }
      if (!term) return true;
      return (
        (l.name && l.name.toLowerCase().includes(term)) ||
        (l.number && String(l.number).includes(term)) ||
        (l.agentName && l.agentName.toLowerCase().includes(term)) ||
        (l.chassis && l.chassis.toLowerCase().includes(term)) ||
        (l.carMake && l.carMake.toLowerCase().includes(term)) ||
        (l.carModel && l.carModel.toLowerCase().includes(term))
      );
    });
  }, [ledgers, searchTerm, agentFilter]);

  const fetchPayments = async (saleId) => {
    try {
      const { data } = await api.get(`/sales/${saleId}/payments`);
      setPayments(data);
    } catch (err) {
      toast.error("Failed to load payments");
    }
  };

  // ── Open Invoice from an existing Sale ────────────────────────
  const handleOpenInvoice = (l) => {
    const saleId = l.saleId || (selectedSale ? selectedSale.saleId : 0);
    const cif = Number(l.cifPrice || selectedSale?.cifPrice || 0);
    const bal = Number(l.balance !== undefined ? l.balance : (selectedSale ? selectedSale.balance : cif));

    setInvoiceData({
      invoiceNumber: generateInvoiceNumber(saleId),
      customerName: cleanCustomerName(l.name || selectedSale?.name || ""),
      customerAddress: "NASSAU, BAHAMAS",
      customerContact: l.number || selectedSale?.number || "",
      issueDate: new Date().toISOString().slice(0, 10),
      dueDate: new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10),
      cars: [
        {
          maker: l.carMake || selectedSale?.carMake || "",
          model: l.carModel || selectedSale?.carModel || "",
          year: l.carYear || selectedSale?.carYear || "",
          chassis: l.chassis || selectedSale?.chassis || "",
          cc: "1500",
          transmission: "AUTOMATIC",
          amount: moneyInput(cif),
        },
      ],
      amount: moneyInput(cif),
      balance: moneyInput(bal),
      paymentLabel: bal <= 0 ? "CIF AMOUNT" : "BALANCE PAYMENT AMOUNT",
      docType: "PRO-FORMA INVOICE",
    });
    setShowInvoiceModal(true);
  };

  // ── Open Standalone Invoice for Non-Sale / Direct Buyer ────────
  const handleOpenBlankInvoice = () => {
    const freshNum = generateInvoiceNumber(Math.floor(1 + Math.random() * 9999));
    setInvoiceData({
      invoiceNumber: freshNum,
      customerName: "",
      customerAddress: "NASSAU, BAHAMAS",
      customerContact: "",
      issueDate: new Date().toISOString().slice(0, 10),
      dueDate: new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10),
      cars: [
        {
          maker: "",
          model: "",
          year: "",
          chassis: "",
          cc: "1500",
          transmission: "AUTOMATIC",
          amount: "0.00",
        },
      ],
      amount: "0.00",
      balance: "0.00",
      paymentLabel: "BALANCE PAYMENT AMOUNT",
      docType: "PRO-FORMA INVOICE",
    });
    setShowInvoiceModal(true);
  };

  // ── Multi-Vehicle Handlers ─────────────────────────────────────
  const handleAddCar = () => {
    setInvoiceData((prev) => ({
      ...prev,
      cars: [
        ...prev.cars,
        {
          maker: "",
          model: "",
          year: "",
          chassis: "",
          cc: "1500",
          transmission: "AUTOMATIC",
          amount: "0.00",
        },
      ],
    }));
  };

  const handleRemoveCar = (index) => {
    if (invoiceData.cars.length <= 1) return;
    setInvoiceData((prev) => {
      const updatedCars = prev.cars.filter((_, i) => i !== index);
      const totalAmount = updatedCars.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      return {
        ...prev,
        cars: updatedCars,
        amount: moneyInput(totalAmount),
        balance: moneyInput(totalAmount),
      };
    });
  };

  const handleCarChange = (index, field, value) => {
    setInvoiceData((prev) => {
      const updatedCars = [...prev.cars];
      updatedCars[index] = { ...updatedCars[index], [field]: value };
      
      // Auto recalculate total amount when an amount is typed
      if (field === "amount") {
        const totalAmount = updatedCars.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
        return {
          ...prev,
          cars: updatedCars,
          amount: moneyInput(totalAmount),
          balance: moneyInput(totalAmount),
        };
      }
      return { ...prev, cars: updatedCars };
    });
  };

  const regenerateNumber = () => {
    const sid = selectedSale?.saleId || Math.floor(1 + Math.random() * 999);
    const freshNum = generateInvoiceNumber(sid);
    setInvoiceData((prev) => ({ ...prev, invoiceNumber: freshNum }));
    toast.success("Generated fresh Invoice Reference: " + freshNum);
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sales", {
        leadId: selectedCustomer.leadId,
        carId: saleForm.carId,
        cifPrice: saleForm.cifPrice,
        saleDate: saleForm.saleDate,
      });
      toast.success("Sale Recorded!");
      setShowSaleModal(false);
      fetchLedgers();
    } catch (err) {
      toast.error("Failed to add sale");
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    try {
      await api.post(`/sales/${selectedSale.saleId}/payments`, paymentForm);
      toast.success("Payment Logged Successfully!");
      setPaymentForm({
        amount: "",
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: "Bank Wire (Mizuho)",
        remarks: "",
      });
      fetchPayments(selectedSale.saleId);
      fetchLedgers();
    } catch (err) {
      toast.error("Failed to add payment");
    }
  };

  const handleUpdateSale = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/sales/${selectedSale.saleId}`, editSaleForm);
      toast.success("Ledger Updated!");
      fetchLedgers();
    } catch (err) {
      toast.error("Failed to update ledger");
    }
  };

  const handleDeleteSale = async () => {
    if (
      !window.confirm(
        "Are you sure you want to completely delete this sale and all its payments? This action cannot be undone."
      )
    )
      return;
    try {
      await api.delete(`/sales/${selectedSale.saleId}`);
      toast.success("Sale Deleted");
      setShowLedgerModal(false);
      fetchLedgers();
    } catch (err) {
      toast.error("Failed to delete sale");
    }
  };

  const openLedger = (ledger) => {
    setSelectedSale(ledger);
    setEditSaleForm({
      cifPrice: ledger.cifPrice || "",
      shipping_status: ledger.shipping_status || "Pending",
      next_payment_reminder: ledger.next_payment_reminder
        ? ledger.next_payment_reminder.slice(0, 10)
        : "",
      notes: ledger.notes || "",
    });
    fetchPayments(ledger.saleId);
    setShowLedgerModal(true);
  };

  // ── Metric Computations ─────────────────────────────────────────
  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const cifPrice = Number(editSaleForm.cifPrice || selectedSale?.cifPrice) || 0;
  const currentBalance = Math.max(cifPrice - totalPaid, 0);
  const paidPercent = cifPrice > 0 ? Math.min(Math.round((totalPaid / cifPrice) * 100), 100) : 0;

  // Global Page Metrics
  const totalCustomers = ledgers.length;
  const totalCIFVolume = ledgers.reduce((sum, l) => sum + (Number(l.cifPrice) || 0), 0);
  const totalCollectedAll = ledgers.reduce((sum, l) => sum + (Number(l.totalPaid) || 0), 0);
  const totalOutstandingAll = ledgers.reduce((sum, l) => sum + Math.max(Number(l.balance) || 0, 0), 0);

  return (
    <div className="mc-page">
      {/* ── Top Header & Global Actions ── */}
      <div className="mc-header" style={{ marginBottom: 14 }}>
        <div className="mc-title-box">
          <div className="d-flex align-items-center gap-2">
            <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
              {totalCustomers} Customer Accounts
            </span>
            <span className="text-muted small d-none d-md-inline">
              Export contracts, milestone tracking & commercial invoices
            </span>
          </div>
        </div>
        <div className="mc-actions">
          <button
            type="button"
            className="btn-standalone-invoice"
            onClick={handleOpenBlankInvoice}
            title="Create an invoice for any buyer without a pre-existing sale"
          >
            <Receipt size={17} /> + New Commercial Invoice
          </button>
          <select
            className="form-select form-select-sm shadow-sm"
            style={{ width: "160px", height: "38px", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "13px" }}
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
          >
            <option value="">All Agents</option>
            {uniqueAgents.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <Form.Control
            type="search"
            placeholder="Search customer, phone, agent, chassis..."
            className="mc-search-input shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Executive KPI Summary Cards ── */}
      <div className="mc-kpi-grid">
        <div className="mc-kpi-card">
          <div className="mc-kpi-icon-wrap" style={{ background: "rgba(59, 130, 246, 0.12)", color: "#2563eb" }}>
            <Users size={22} />
          </div>
          <div>
            <div className="mc-kpi-label">Total Accounts</div>
            <div className="mc-kpi-val">{totalCustomers}</div>
          </div>
        </div>

        <div className="mc-kpi-card">
          <div className="mc-kpi-icon-wrap" style={{ background: "rgba(99, 102, 241, 0.12)", color: "#6366f1" }}>
            <TrendingUp size={22} />
          </div>
          <div>
            <div className="mc-kpi-label">Gross CIF Volume</div>
            <div className="mc-kpi-val">${totalCIFVolume.toLocaleString()}</div>
          </div>
        </div>

        <div className="mc-kpi-card">
          <div className="mc-kpi-icon-wrap" style={{ background: "rgba(16, 185, 129, 0.12)", color: "#059669" }}>
            <CheckCircle2 size={22} />
          </div>
          <div>
            <div className="mc-kpi-label">Total Collected</div>
            <div className="mc-kpi-val text-success">${totalCollectedAll.toLocaleString()}</div>
          </div>
        </div>

        <div className="mc-kpi-card">
          <div className="mc-kpi-icon-wrap" style={{ background: "rgba(239, 68, 68, 0.12)", color: "#dc2626" }}>
            <Clock size={22} />
          </div>
          <div>
            <div className="mc-kpi-label">Outstanding Receivables</div>
            <div className="mc-kpi-val text-danger">${totalOutstandingAll.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* ── Customer Accounts Data Grid ── */}
      <div className="mc-table-card">
        <Table responsive hover className="mc-table align-middle">
          <thead className="mc-thead">
            <tr>
              <th>Client / Account</th>
              <th>Assigned Agent</th>
              <th>Vehicle Specification</th>
              <th>Contract CIF</th>
              <th>Paid</th>
              <th>Balance Due</th>
              <th>Logistics Status</th>
              <th>Payment Reminder</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLedgers.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-5 text-muted">
                  No customer records found matching your filters.
                </td>
              </tr>
            ) : null}
            {filteredLedgers.map((l) => (
                <tr key={l.leadId + (l.saleId || "none")} className="mc-row">
                  <td>
                    <div className="mc-client-cell">
                      <div className="mc-avatar">{getInitials(l.name)}</div>
                      <div>
                        <div className="fw-bold">{cleanCustomerName(l.name)}</div>
                        <div className="small text-muted font-monospace">{l.number || "No contact"}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="fw-semibold text-secondary">{l.agentName || "Unassigned"}</span>
                  </td>
                  <td>
                    {l.saleId ? (
                      <div className="mc-vehicle-pill">
                        <Car size={13} className="text-primary" />
                        <span>
                          {l.carMake || l.carModel
                            ? [l.carYear, l.carMake, l.carModel].filter(Boolean).join(" ")
                            : `Vehicle #${l.carId}`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted fst-italic small">No vehicle attached</span>
                    )}
                  </td>
                  <td className="fw-bold text-primary">
                    {l.cifPrice ? `$${Number(l.cifPrice).toLocaleString()}` : "—"}
                  </td>
                  <td className="text-success fw-bold">
                    {l.totalPaid ? `$${Number(l.totalPaid).toLocaleString()}` : "$0"}
                  </td>
                  <td>
                    {l.saleId ? (
                      <span
                        className={`fw-bold ${
                          Number(l.balance) <= 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        ${Number(l.balance).toLocaleString()}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {l.shipping_status === "Delivered" ? (
                      <Badge bg="success" className="px-2 py-1">Delivered</Badge>
                    ) : l.shipping_status === "In Transit" ? (
                      <Badge bg="info" className="px-2 py-1">In Transit</Badge>
                    ) : l.shipping_status === "Arrived" ? (
                      <Badge bg="primary" className="px-2 py-1">Arrived</Badge>
                    ) : l.saleId ? (
                      <Badge bg="warning" text="dark" className="px-2 py-1">Pending</Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="small font-monospace text-muted">
                    {l.next_payment_reminder
                      ? new Date(l.next_payment_reminder).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="text-end">
                    {!l.saleId ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="fw-semibold"
                        onClick={() => {
                          setSelectedCustomer(l);
                          setShowSaleModal(true);
                        }}
                      >
                        <Plus size={14} className="me-1" /> Bind Sale
                      </Button>
                    ) : (
                      <div className="d-inline-flex gap-2">
                        <Button size="sm" variant="dark" className="fw-semibold" onClick={() => openLedger(l)}>
                          Open Ledger
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleOpenInvoice(l)}
                          title="Generate Invoice / Receipt for this contract"
                        >
                          <FileText size={15} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          EXECUTIVE FINTECH LEDGER MODAL
          ═══════════════════════════════════════════════════════════════ */}
      <Modal
        show={showLedgerModal}
        onHide={() => setShowLedgerModal(false)}
        size="lg"
        centered
        dialogClassName="ledger-modal-dialog"
        contentClassName="ledger-modal-content"
      >
        <div className="ledger-header">
          <div className="ledger-title-box">
            <div className="ledger-title-icon">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h5 className="fw-bold mb-0">Customer Ledger · {cleanCustomerName(selectedSale?.name)}</h5>
              <div className="small text-white-50">
                Vehicle: {selectedSale?.carYear} {selectedSale?.carMake} {selectedSale?.carModel} (Chassis: {selectedSale?.chassis || "—"})
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowLedgerModal(false)}
          />
        </div>

        <Modal.Body className="p-4">
          {/* Top Metric Cards */}
          <div className="ledger-stats-grid">
            <div className="ledger-stat-card">
              <div className="ledger-stat-label">
                <DollarSign size={14} className="text-primary" /> Agreed CIF Total
              </div>
              <div className="ledger-stat-val text-primary">${cifPrice.toLocaleString()}</div>
              <div className="small text-muted mt-1">Full Export Contract Price</div>
            </div>

            <div className="ledger-stat-card">
              <div className="ledger-stat-label">
                <CheckCircle2 size={14} className="text-success" /> Total Collected
              </div>
              <div className="ledger-stat-val text-success">${totalPaid.toLocaleString()}</div>
              <div className="ledger-progress-bar">
                <div className="ledger-progress-fill" style={{ width: `${paidPercent}%` }} />
              </div>
              <div className="small text-muted mt-1 d-flex justify-content-between">
                <span>{paidPercent}% of CIF</span>
                <span>{payments.length} Remittance{payments.length === 1 ? '' : 's'}</span>
              </div>
            </div>

            <div className="ledger-stat-card">
              <div className="ledger-stat-label">
                <Clock size={14} className={currentBalance <= 0 ? "text-success" : "text-danger"} /> Balance Outstanding
              </div>
              <div className={`ledger-stat-val ${currentBalance <= 0 ? "text-success" : "text-danger"}`}>
                ${currentBalance.toLocaleString()}
              </div>
              <div className="mt-1">
                {currentBalance <= 0 ? (
                  <Badge bg="success" className="px-2 py-1">PAID IN FULL</Badge>
                ) : totalPaid > 0 ? (
                  <Badge bg="warning" text="dark" className="px-2 py-1">PARTIALLY PAID</Badge>
                ) : (
                  <Badge bg="danger" className="px-2 py-1">PAYMENT PENDING</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Action Strip */}
          <div className="ledger-action-strip">
            <div className="d-flex align-items-center gap-2">
              <Sparkles size={16} className="text-primary" />
              <span className="small fw-bold">Commercial Actions:</span>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn-generate-invoice-hero"
                onClick={() => handleOpenInvoice(selectedSale)}
              >
                <FileText size={16} /> Generate Pro-Forma Invoice / Receipt
              </button>
              <Button
                variant="outline-danger"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={handleDeleteSale}
              >
                <Trash2 size={15} /> Delete Sale
              </Button>
            </div>
          </div>

          {/* Two-Column Working Area */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="ledger-section-card">
                <div className="ledger-section-title text-primary">
                  <Truck size={16} /> Vehicle &amp; Shipping Status
                </div>
                <Form onSubmit={handleUpdateSale}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted mb-1">Contract CIF Price ($)</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={editSaleForm.cifPrice}
                      onChange={(e) => setEditSaleForm({ ...editSaleForm, cifPrice: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted mb-1">Shipping Milestone</Form.Label>
                    <Form.Select
                      size="sm"
                      value={editSaleForm.shipping_status}
                      onChange={(e) => setEditSaleForm({ ...editSaleForm, shipping_status: e.target.value })}
                    >
                      <option value="Pending">Pending Dispatch</option>
                      <option value="In Transit">In Transit (On Vessel)</option>
                      <option value="Arrived">Arrived at Port</option>
                      <option value="Delivered">Delivered &amp; Cleared</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted mb-1">Payment Reminder Date</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={editSaleForm.next_payment_reminder}
                      onChange={(e) => setEditSaleForm({ ...editSaleForm, next_payment_reminder: e.target.value })}
                    />
                  </Form.Group>
                  <Button size="sm" type="submit" variant="primary" className="w-100 fw-semibold">
                    Save Logistics Updates
                  </Button>
                </Form>
              </div>
            </div>

            <div className="col-md-6">
              <div className="ledger-section-card">
                <div className="ledger-section-title text-success">
                  <CreditCard size={16} /> Record Customer Payment
                </div>
                <Form onSubmit={handleAddPayment}>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <Form.Label className="small fw-semibold text-muted mb-1">Amount ($)</Form.Label>
                      <Form.Control
                        size="sm"
                        required
                        type="number"
                        placeholder="0.00"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <Form.Label className="small fw-semibold text-muted mb-1">Date</Form.Label>
                      <Form.Control
                        size="sm"
                        required
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted mb-1">Remittance Channel</Form.Label>
                    <Form.Select
                      size="sm"
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    >
                      <option value="Bank Wire (Mizuho)">Bank Wire (Mizuho Bank Ltd)</option>
                      <option value="Bank Wire (Other)">Bank Wire (Direct / Other)</option>
                      <option value="Telegraphic Transfer (TT)">Telegraphic Transfer (TT)</option>
                      <option value="Letter of Credit (LC)">Letter of Credit (LC)</option>
                      <option value="Cash Deposit">Cash Deposit</option>
                      <option value="Credit Card">Credit Card</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted mb-1">Reference / Remarks</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="e.g. Downpayment / 1st Installment wire"
                      value={paymentForm.remarks}
                      onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                    />
                  </Form.Group>
                  <Button size="sm" type="submit" variant="success" className="w-100 fw-semibold">
                    Submit &amp; Credit Payment
                  </Button>
                </Form>
              </div>
            </div>
          </div>

          {/* Payment History Table (FIXED DATE CHIP) */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0 text-uppercase small letter-spacing-1">
              Payment Transactions &amp; Receipts ({payments.length})
            </h6>
          </div>
          <div className="ledger-history-wrap">
            <Table hover size="sm" className="ledger-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount Credited</th>
                  <th>Remittance Method</th>
                  <th>Remarks / Notes</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-muted">
                      No payments recorded yet for this contract.
                    </td>
                  </tr>
                ) : null}
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="ledger-date-chip">
                        {new Date(p.payment_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="fw-bold text-success font-monospace">
                      +${Number(p.amount).toLocaleString()}
                    </td>
                    <td>
                      <Badge bg="secondary" className="fw-normal">{p.payment_method}</Badge>
                    </td>
                    <td className="text-muted small">{p.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════
          EXECUTIVE INVOICE STUDIO (WITH MULTI-VEHICLE SUPPORT & LIVE PREVIEW)
          ═══════════════════════════════════════════════════════════════ */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="xl"
        centered
        dialogClassName="invoice-modal-dialog"
        contentClassName="invoice-modal-content"
      >
        <div className="invoice-modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="invoice-icon-wrap">
              <FileText size={20} />
            </div>
            <div>
              <h5 className="fw-bold mb-0 text-white">Commercial Invoice &amp; Receipt Studio</h5>
              <div className="small text-white-50">
                Live Pro-Forma Invoice &amp; Payment Receipt Generator · Multi-Vehicle Supported
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <PDFDownloadLink
              key={showInvoiceModal ? JSON.stringify(invoiceData) : "closed"}
              document={<InvoicePDF data={invoiceData} />}
              fileName={`${invoiceData.invoiceNumber || "Invoice"}.pdf`}
            >
              {({ loading }) => (
                <button
                  type="button"
                  disabled={loading}
                  className="btn-invoice-download text-white"
                >
                  <Download size={15} /> {loading ? "Rendering Document..." : "Download Official PDF"}
                </button>
              )}
            </PDFDownloadLink>
            <button
              type="button"
              className="btn-close ms-2"
              onClick={() => setShowInvoiceModal(false)}
            />
          </div>
        </div>

        <Modal.Body className="p-0">
          <div className="row g-0">
            {/* Left Parameter Editor */}
            <div className="col-lg-5 p-4 border-end invoice-form-pane">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="small fw-bold text-uppercase text-muted">Invoice Particulars</span>
                <Badge bg="primary-subtle" className="text-primary border border-primary-subtle">
                  Live Syncing
                </Badge>
              </div>

              <div className="row g-2">
                {/* Invoice Number with Quick Regenerate */}
                <div className="col-12 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1 d-flex justify-content-between">
                    <span>Invoice Reference No.</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none text-primary d-flex align-items-center gap-1"
                      onClick={regenerateNumber}
                      title="Generate a brand new unique reference"
                    >
                      <RotateCw size={13} /> <span>Regenerate No.</span>
                    </button>
                  </Form.Label>
                  <div className="input-group input-group-sm">
                    <Form.Control
                      type="text"
                      className="font-monospace fw-bold"
                      value={invoiceData.invoiceNumber}
                      onChange={(e) =>
                        setInvoiceData({ ...invoiceData, invoiceNumber: e.target.value })
                      }
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={regenerateNumber}
                      title="Generate New Number"
                    >
                      <RefreshCw size={14} />
                    </Button>
                  </div>
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Document Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={invoiceData.docType}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, docType: e.target.value })
                    }
                  >
                    <option value="PRO-FORMA INVOICE">Pro-Forma Invoice</option>
                    <option value="PAYMENT RECEIPT">Payment Receipt</option>
                  </Form.Select>
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Amount Callout Label</Form.Label>
                  <Form.Select
                    size="sm"
                    value={invoiceData.paymentLabel}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, paymentLabel: e.target.value })
                    }
                  >
                    <option value="BALANCE PAYMENT AMOUNT">Balance Payment Amount</option>
                    <option value="DOWNPAYMENT AMOUNT">Downpayment Amount</option>
                    <option value="CIF AMOUNT">CIF Amount</option>
                    <option value="TOTAL DUE">Total Due</option>
                  </Form.Select>
                </div>

                <div className="col-12 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Customer / Consignee Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="e.g. MKS CUSTOMS BROKERAGE"
                    value={invoiceData.customerName}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, customerName: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Customer Address</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="e.g. NASSAU, BAHAMAS"
                    value={invoiceData.customerAddress}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, customerAddress: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Customer Contact</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="e.g. +1 (242) 471 0118"
                    value={invoiceData.customerContact}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, customerContact: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Issue Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={invoiceData.issueDate}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, issueDate: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Due Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={invoiceData.dueDate}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, dueDate: e.target.value })
                    }
                  />
                </div>

                {/* ── Multi-Vehicle Line Items ── */}
                <div className="col-12 mt-3 mb-1">
                  <div className="d-flex justify-content-between align-items-center border-top pt-2">
                    <span className="small fw-bold text-uppercase text-muted">
                      Vehicle Specifications ({invoiceData.cars.length})
                    </span>
                    <Badge bg="secondary" className="small">Multi-Car Supported</Badge>
                  </div>
                </div>

                {invoiceData.cars.map((car, idx) => (
                  <div key={idx} className="col-12 vehicle-item-box">
                    <div className="vehicle-item-header">
                      <span className="small fw-bold text-primary">Vehicle #{idx + 1}</span>
                      {invoiceData.cars.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={() => handleRemoveCar(idx)}
                          title="Remove this vehicle"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <Form.Label className="small text-muted mb-0">Maker</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g. HONDA"
                          value={car.maker}
                          onChange={(e) => handleCarChange(idx, "maker", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <Form.Label className="small text-muted mb-0">Model</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g. CR-V"
                          value={car.model}
                          onChange={(e) => handleCarChange(idx, "model", e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="small text-muted mb-0">Year</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g. 2021"
                          value={car.year}
                          onChange={(e) => handleCarChange(idx, "year", e.target.value)}
                        />
                      </div>
                      <div className="col-8">
                        <Form.Label className="small text-muted mb-0">Chassis ID</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g. RT5-1202112"
                          value={car.chassis}
                          onChange={(e) => handleCarChange(idx, "chassis", e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="small text-muted mb-0">Engine CC</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g. 1500"
                          value={car.cc}
                          onChange={(e) => handleCarChange(idx, "cc", e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="small text-muted mb-0">Transmission</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="AUTOMATIC"
                          value={car.transmission}
                          onChange={(e) => handleCarChange(idx, "transmission", e.target.value)}
                        />
                      </div>
                      <div className="col-4">
                        <Form.Label className="small text-muted mb-0">Price ($)</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="8200"
                          value={car.amount}
                          onChange={(e) => handleCarChange(idx, "amount", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="col-12 mb-3">
                  <button
                    type="button"
                    className="btn-add-vehicle-line"
                    onClick={handleAddCar}
                  >
                    <Plus size={15} /> + Add Another Vehicle to Invoice
                  </button>
                </div>

                {/* Financial Callout */}
                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Calculated Total CIF ($)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={invoiceData.amount}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, amount: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-6 mb-2">
                  <Form.Label className="small fw-bold text-muted mb-1">Final Highlight Callout ($)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={invoiceData.balance}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, balance: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Right Live Document Preview Pane */}
            <div className="col-lg-7 p-3 invoice-preview-pane">
              <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                <span className="small fw-bold text-muted text-uppercase d-flex align-items-center gap-1">
                  <Eye size={14} className="text-primary" /> Live Document Preview
                </span>
                <span className="badge bg-secondary-subtle text-secondary small">
                  A4 Landscape · Page 1: Commercial / Page 2: Terms
                </span>
              </div>
              <div className="invoice-viewer-wrapper shadow-sm rounded border">
                <PDFViewer width="100%" height="600px" showToolbar={true}>
                  <InvoicePDF data={invoiceData} />
                </PDFViewer>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Record Sale Modal ── */}
      <Modal show={showSaleModal} onHide={() => setShowSaleModal(false)} centered>
        <Form onSubmit={handleCreateSale}>
          <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold">Record Vehicle Sale · {selectedCustomer?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Select Car from Available Stock</Form.Label>
              <Form.Select
                required
                value={saleForm.carId}
                onChange={(e) => setSaleForm({ ...saleForm, carId: e.target.value })}
              >
                <option value="">-- Choose Car --</option>
                {stock.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.make} {c.model} (Year: {c.year}, Chassis: {c.chassis})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Agreed CIF Price ($)</Form.Label>
              <Form.Control
                required
                type="number"
                placeholder="e.g. 8500"
                value={saleForm.cifPrice}
                onChange={(e) => setSaleForm({ ...saleForm, cifPrice: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Sale Agreement Date</Form.Label>
              <Form.Control
                required
                type="date"
                value={saleForm.saleDate}
                onChange={(e) => setSaleForm({ ...saleForm, saleDate: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowSaleModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="fw-semibold px-4">
              Confirm &amp; Bind Sale
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MyCustomers;
